import { z } from "zod";
import { localStore } from "../../../lib/store";

const ContentFiltersSchema = z.object({
  specialtyIds: z.array(z.string()),
  specialtyUids: z.array(z.string()),
  subSpecialtyIds: z.array(z.string()),
  subSpecialtyUids: z.array(z.string()),
  // Room discovery filter (JuisciTopic uids). `.default([])` so a blob persisted
  // before topic filtering existed still parses instead of resetting the record.
  topicUids: z.array(z.string()).default([]),
  // How the selected topics combine: "any" (union) or "all" (intersection).
  // Defaults to "any" (the original behavior) and upgrades old blobs in place.
  topicMatch: z.enum(["any", "all"]).default("any"),
  // Same union/intersection choice for the tag fallback filter (un-migrated
  // rooms). Separate from topicMatch so a room's two modes don't clobber it.
  tagMatch: z.enum(["any", "all"]).default("any"),
});

type ContentFilters = z.infer<typeof ContentFiltersSchema>;

const ContentFiltersPayloadSchema = z.object({
  specialtyIds: z.array(z.string()).optional(),
  specialtyUids: z.array(z.string()).optional(),
  subSpecialtyIds: z.array(z.string()).optional(),
  subSpecialtyUids: z.array(z.string()).optional(),
  topicUids: z.array(z.string()).optional(),
  topicMatch: z.enum(["any", "all"]).optional(),
  tagMatch: z.enum(["any", "all"]).optional(),
});

type ContentFiltersPayload = z.infer<typeof ContentFiltersPayloadSchema>;

const initialContentFilters: ContentFilters = {
  specialtyIds: [],
  specialtyUids: [],
  subSpecialtyIds: [],
  subSpecialtyUids: [],
  topicUids: [],
  topicMatch: "any",
  tagMatch: "any",
};

const contentFiltersStore = localStore<Record<string, ContentFilters>>(
  "contentFilters",
  {},
  z.record(z.string(), ContentFiltersSchema)
);

export function getStoredContentFilters(): ContentFilters {
  const stored = contentFiltersStore.get()[globalThis.location.pathname];
  return stored === undefined ? initialContentFilters : stored;
}

export function storeContentFilters(payload: ContentFiltersPayload) {
  contentFiltersStore.set((prev) => ({
    ...prev,
    [globalThis.location.pathname]: {
      specialtyIds:
        payload.specialtyIds === undefined ? [] : payload.specialtyIds,
      specialtyUids:
        payload.specialtyUids === undefined ? [] : payload.specialtyUids,
      subSpecialtyIds:
        payload.subSpecialtyIds === undefined ? [] : payload.subSpecialtyIds,
      subSpecialtyUids:
        payload.subSpecialtyUids === undefined ? [] : payload.subSpecialtyUids,
      topicUids: payload.topicUids === undefined ? [] : payload.topicUids,
      topicMatch: payload.topicMatch === undefined ? "any" : payload.topicMatch,
      tagMatch: payload.tagMatch === undefined ? "any" : payload.tagMatch,
    },
  }));
}

export function clearStoredContentFilters() {
  contentFiltersStore.set({});
}
